import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Package, Plus, Send, Trash2, ChevronRight, FlaskConical, Upload, AlertTriangle, CheckCircle } from 'lucide-react';
import { PageHeader, StatusBadge, EmptyState, DataTableSkeleton } from '../components/ui';
import { useToast, ToastContainer } from '../components/ui';
import { API_BASE } from '../api';
import { useDemo } from '../context/DemoContext';
import { DEMO_VENDOR_PRODUCTS } from '../demo/data';

type VendorProductStatus = 'draft' | 'pending_review' | 'approved' | 'active' | 'paused' | 'archived';

interface VendorProduct {
  id: string;
  title: string;
  title_zh: string | null;
  category: string;
  price_min: number | null;
  price_max: number | null;
  status: VendorProductStatus;
  moq: number;
  is_test: boolean;
  shopify_product_id: string | null;
  shopify_status: string | null;
  created_at: string;
  updated_at: string;
}

interface InventoryItem {
  id: string;
  product_title: string;
  sku: string;
  title: string;
  inventory_quantity: number;
}

const statusVariantMap: Record<VendorProductStatus, 'success' | 'warning' | 'error' | 'info' | 'neutral'> = {
  draft: 'neutral',
  pending_review: 'warning',
  approved: 'info',
  active: 'success',
  paused: 'warning',
  archived: 'neutral',
};

const DEMO_INVENTORY: InventoryItem[] = [
  { id: 'v1', product_title: 'Premium Bamboo Tote Bag', sku: 'BBT-001', title: 'Natural', inventory_quantity: 420 },
  { id: 'v2', product_title: 'Organic Cotton Hoodie', sku: 'OCH-S-OAT', title: 'Small / Oat', inventory_quantity: 12 },
  { id: 'v3', product_title: 'Organic Cotton Hoodie', sku: 'OCH-M-OAT', title: 'Medium / Oat', inventory_quantity: 0 },
  { id: 'v4', product_title: 'Minimalist Phone Stand', sku: 'MPS-BLK', title: 'Black', inventory_quantity: 310 },
  { id: 'v5', product_title: 'Recycled Denim Crossbody', sku: 'RDC-001', title: 'Classic', inventory_quantity: 45 },
  { id: 'v6', product_title: 'Ceramic Tumbler Set', sku: 'CTS-002', title: 'Set of 2', inventory_quantity: 200 },
  { id: 'v7', product_title: 'Bamboo Cutlery Travel Kit', sku: 'BCK-001', title: 'Standard', inventory_quantity: 8 },
];

function stockStatus(qty: number): { labelKey: string; color: string; variant: 'success' | 'warning' | 'error' } {
  if (qty === 0) return { labelKey: 'inventory:outOfStock', color: '#f87171', variant: 'error' };
  if (qty <= 20) return { labelKey: 'inventory:lowStock', color: '#eab308', variant: 'warning' };
  return { labelKey: 'inStock', color: '#4ade80', variant: 'success' };
}

export default function VendorProducts() {
  const { t, i18n } = useTranslation(['products', 'inventory', 'common']);
  const { toasts, addToast, removeToast } = useToast();
  const navigate = useNavigate();
  const { isDemoMode } = useDemo();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = searchParams.get('tab') === 'inventory' ? 'inventory' : 'products';
  const setTab = (tab: string) => {
    const p = new URLSearchParams(searchParams);
    if (tab === 'products') p.delete('tab'); else p.set('tab', tab);
    setSearchParams(p, { replace: true });
  };

  const [products, setProducts] = useState<VendorProduct[]>([]);
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [showTestOnly, setShowTestOnly] = useState(false);

  const fetchProducts = async () => {
    if (isDemoMode) {
      const filtered = DEMO_VENDOR_PRODUCTS.filter(p =>
        (!statusFilter || p.status === statusFilter) && (!showTestOnly || p.is_test)
      );
      setProducts(filtered as VendorProduct[]);
      setLoading(false);
      return;
    }
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (showTestOnly) params.set('is_test', 'true');
      const qs = params.toString() ? `?${params}` : '';
      const res = await fetch(`${API_BASE}/vendor/products${qs}`, { credentials: 'include' });
      const json = await res.json();
      if (res.ok) setProducts(json.data || []);
    } catch { addToast(t('common:error'), 'error'); }
    finally { setLoading(false); }
  };

  const fetchInventory = async () => {
    if (isDemoMode) { setInventory(DEMO_INVENTORY); setLoading(false); return; }
    try {
      const res = await fetch(`${API_BASE}/vendor/inventory`, { credentials: 'include' });
      const json = await res.json();
      if (res.ok) setInventory(json.data || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    setLoading(true);
    if (activeTab === 'inventory') fetchInventory();
    else fetchProducts();
  }, [statusFilter, showTestOnly, isDemoMode, activeTab]);

  const handleSubmit = async (productId: string) => {
    try {
      const res = await fetch(`${API_BASE}/vendor/products/${productId}/submit`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' } });
      if (res.ok) { fetchProducts(); addToast(t('common:success'), 'success'); }
    } catch { addToast(t('common:error'), 'error'); }
  };

  const handleDelete = async (productId: string) => {
    try {
      const res = await fetch(`${API_BASE}/vendor/products/${productId}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { fetchProducts(); addToast(t('common:success'), 'success'); }
    } catch { addToast(t('common:error'), 'error'); }
  };

  const isZh = i18n.language === 'zh';
  const statusOptions: VendorProductStatus[] = ['draft', 'pending_review', 'approved', 'active', 'paused', 'archived'];

  const tabBtn = (key: string, label: string) => (
    <button onClick={() => setTab(key)}
      className={`px-4 py-2 text-sm font-medium transition-colors relative ${activeTab === key ? 'text-[#FFD400]' : 'text-white/45 hover:text-white/70'}`}>
      {label}
      {activeTab === key && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#FFD400]" />}
    </button>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('products:title')}
        actions={activeTab === 'products' ? (
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/vendor/products/import-csv')} className="btn-ghost flex items-center gap-2 text-sm">
              <Upload className="w-4 h-4" />{t('products:uploadCsv')}
            </button>
            <button onClick={() => navigate('/vendor/products/new')} className="btn-primary flex items-center gap-2">
              <Plus className="w-4 h-4" />{t('products:addNew')}
            </button>
          </div>
        ) : undefined}
      />

      {/* Tabs */}
      <div className="flex gap-1" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        {tabBtn('products', t('products:title'))}
        {tabBtn('inventory', t('inventory:title'))}
      </div>

      {activeTab === 'products' ? (
        <>
          {/* Filters */}
          <div className="flex gap-2 flex-wrap items-center">
            <button onClick={() => setStatusFilter('')}
              className={`text-xs px-3 py-1.5 transition-colors ${!statusFilter ? 'text-[#0B0B0B]' : 'text-white/50'}`}
              style={{ backgroundColor: !statusFilter ? '#FFD400' : 'rgba(255,255,255,0.04)', border: `1px solid ${!statusFilter ? '#FFD400' : 'rgba(255,255,255,0.08)'}`, borderRadius: '2px' }}>
              {t('common:all')}
            </button>
            {statusOptions.map(s => (
              <button key={s} onClick={() => setStatusFilter(s)}
                className={`text-xs px-3 py-1.5 transition-colors ${statusFilter === s ? 'text-[#0B0B0B]' : 'text-white/50'}`}
                style={{ backgroundColor: statusFilter === s ? '#FFD400' : 'rgba(255,255,255,0.04)', border: `1px solid ${statusFilter === s ? '#FFD400' : 'rgba(255,255,255,0.08)'}`, borderRadius: '2px' }}>
                {t(`products:status.${s}`)}
              </button>
            ))}
            <div className="ml-auto">
              <label className="flex items-center gap-1.5 text-xs text-white/40 cursor-pointer">
                <input type="checkbox" checked={showTestOnly} onChange={(e) => setShowTestOnly(e.target.checked)} className="accent-[#FFD400]" />
                <FlaskConical className="w-3 h-3" />{t('products:isTest')}
              </label>
            </div>
          </div>

          {/* Product list */}
          {loading ? <DataTableSkeleton rows={5} cols={5} /> : products.length === 0 ? (
            <EmptyState icon={<Package className="w-8 h-8" />} message={t('products:empty')} />
          ) : (
            <div className="space-y-2">
              {products.map(product => (
                <div key={product.id} onClick={() => navigate(`/vendor/products/${product.id}/edit`)}
                  className="flex items-center gap-4 p-4 group transition-colors cursor-pointer"
                  style={{ backgroundColor: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '2px' }}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-white truncate">
                        {isZh && product.title_zh ? product.title_zh : product.title}
                      </p>
                      {product.is_test && (
                        <span className="text-[10px] px-1.5 py-0.5 text-white/40 bg-white/5 rounded" style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
                          <FlaskConical className="w-2.5 h-2.5 inline mr-0.5" />TEST
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-white/30 mt-0.5">{product.category} · MOQ {product.moq}</p>
                  </div>
                  {product.price_min != null && (
                    <p className="text-sm text-white/60 shrink-0">
                      ${product.price_min}{product.price_max != null && product.price_max !== product.price_min ? ` – $${product.price_max}` : ''}
                    </p>
                  )}
                  {product.shopify_product_id && (
                    <span className="text-[10px] px-1.5 py-0.5 text-[#4ade80] bg-[#4ade80]/10 rounded">Shopify</span>
                  )}
                  <StatusBadge variant={statusVariantMap[product.status]}>{t(`products:status.${product.status}`)}</StatusBadge>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                    {product.status === 'draft' && (
                      <>
                        <button onClick={() => handleSubmit(product.id)} className="p-1.5 text-white/30 hover:text-[#FFD400] transition-colors" title={t('products:submitForReview')}>
                          <Send className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => handleDelete(product.id)} className="p-1.5 text-white/30 hover:text-red-400 transition-colors" title={t('common:delete')}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                  <ChevronRight className="w-4 h-4 text-white/15 shrink-0" />
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        /* Inventory Health Tab */
        loading ? <DataTableSkeleton rows={6} cols={5} /> : inventory.length === 0 ? (
          <EmptyState icon={<Package className="w-8 h-8" />} message={t('inventory:empty')} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <colgroup>
                <col style={{ width: '30%' }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '20%' }} />
              </colgroup>
              <thead>
                <tr className="table-header">
                  <th className="text-left px-3 py-2 text-[11px] text-white/40 uppercase">{t('inventory:product')}</th>
                  <th className="text-left px-3 py-2 text-[11px] text-white/40 uppercase">{t('inventory:variant')} / {t('inventory:sku')}</th>
                  <th className="text-right px-3 py-2 text-[11px] text-white/40 uppercase">{t('inventory:currentStock')}</th>
                  <th className="text-center px-3 py-2 text-[11px] text-white/40 uppercase">{t('common:status')}</th>
                  <th className="text-center px-3 py-2 text-[11px] text-white/40 uppercase">{t('inventory:alerts')}</th>
                </tr>
              </thead>
              <tbody>
                {inventory.map(item => {
                  const st = stockStatus(item.inventory_quantity);
                  return (
                    <tr key={item.id} className="table-row">
                      <td className="px-3 py-2.5 text-sm text-white/80">{item.product_title}</td>
                      <td className="px-3 py-2.5">
                        <span className="text-xs text-white/55">{item.title}</span>
                        <span className="text-[10px] text-white/30 ml-2">{item.sku}</span>
                      </td>
                      <td className="px-3 py-2.5 text-right text-sm font-medium text-white">{item.inventory_quantity}</td>
                      <td className="px-3 py-2.5 text-center">
                        <StatusBadge variant={st.variant}>{t(st.labelKey)}</StatusBadge>
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        {item.inventory_quantity <= 20 ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-medium" style={{ color: item.inventory_quantity === 0 ? '#f87171' : '#eab308' }}>
                            <AlertTriangle className="w-3 h-3" />
                            {item.inventory_quantity === 0 ? t('inventory:outOfStock') : t('inventory:lowStock')}
                          </span>
                        ) : (
                          <CheckCircle className="w-3.5 h-3.5 text-white/15 mx-auto" />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
}
